import { Helmet } from 'react-helmet-async';

interface SEOEnhancedProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
  nofollow?: boolean;
  product?: {
    price: number;
    currency: string;
    availability: 'in_stock' | 'out_of_stock' | 'preorder';
    sku?: string;
    brand?: string;
  };
}

export const SEOEnhanced = ({
  title,
  description,
  image = '/icon-512.png',
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = 'website',
  keywords = 'nootropic drinks, energy supplements, cognitive enhancement, SOLR, brain health',
  author = 'SOLR Drinks',
  publishedTime,
  modifiedTime,
  noindex = false,
  nofollow = false,
  product,
}: SEOEnhancedProps) => {
  const fullTitle = `${title} | SOLR - Premium Nootropic Beverages`;
  const robotsContent = `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`;
  
  // Ensure image is absolute URL
  const absoluteImage = image.startsWith('http') 
    ? image 
    : `${typeof window !== 'undefined' ? window.location.origin : ''}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={url} />
      
      {/* Viewport and Mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#9b87f5" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="SOLR" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:site" content="@solr_drinks" />
      <meta name="twitter:creator" content="@solr_drinks" />
      
      {/* Article specific (for blog posts, etc.) */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Product specific */}
      {type === 'product' && product && (
        <>
          <meta property="product:price:amount" content={product.price.toString()} />
          <meta property="product:price:currency" content={product.currency} />
          <meta property="product:availability" content={product.availability} />
          {product.sku && <meta property="product:retailer_item_id" content={product.sku} />}
          {product.brand && <meta property="product:brand" content={product.brand} />}
        </>
      )}
      
      {/* Dublin Core Metadata */}
      <meta name="DC.title" content={title} />
      <meta name="DC.description" content={description} />
      <meta name="DC.creator" content={author} />
      <meta name="DC.language" content="en" />
      
      {/* Geo Tags (for local SEO) */}
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="United Kingdom" />
      
      {/* Performance hints */}
      <link rel="dns-prefetch" href="https://kllsxgvvwosxlcpxqwin.supabase.co" />
      <link rel="preconnect" href="https://kllsxgvvwosxlcpxqwin.supabase.co" crossOrigin="anonymous" />
    </Helmet>
  );
};

// JSON-LD Schema generators
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'SOLR Drinks',
  alternateName: 'SOLR Nutrition',
  url: 'https://solrdrinks.com',
  logo: 'https://solrdrinks.com/icon-512.png',
  description: 'Premium nootropic beverages for optimal cognitive performance',
  email: 'support@solrdrinks.com',
  sameAs: [
    'https://twitter.com/solr_drinks',
    'https://instagram.com/solr_drinks',
    'https://facebook.com/solrdrinks'
  ],
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'GB'
  }
});

export const generateProductSchema = (product: {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  inStock: boolean;
  rating?: number;
  reviewCount?: number;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.image,
  sku: product.id,
  brand: {
    '@type': 'Brand',
    name: 'SOLR'
  },
  offers: {
    '@type': 'Offer',
    url: `https://solrdrinks.com/shop/${product.id}`,
    priceCurrency: 'GBP',
    price: product.price,
    availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    seller: {
      '@type': 'Organization',
      name: 'SOLR Drinks'
    }
  },
  ...(product.rating && product.reviewCount ? {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount,
      bestRating: 5,
      worstRating: 1
    }
  } : {})
});

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

export const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'SOLR Drinks',
  url: 'https://solrdrinks.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://solrdrinks.com/shop?search={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  }
});
